import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
  SlidersHorizontal, Plus, X, Save, RefreshCw, Loader2,
  Users2, Building2, MapPin, BadgeCheck, Boxes, Search, Radar, Sparkles,
  Briefcase, TrendingUp, Mail, Phone, Lightbulb,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// A single add/remove chip field. `recommended` = the count that gives the
// sharpest Apollo reach; users may add MORE (which broadens reach) — we show a
// soft best-practice hint instead of blocking or greying extra chips.
const ChipField = ({ label, icon: Icon, items, onChange, placeholder, testid, recommended, field }) => {
  const [draft, setDraft] = useState('');
  const [sugs, setSugs] = useState([]);
  useEffect(() => {
    if (!field) return;
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`${API}/api/brain/icp/suggest`, { params: { field, q: draft } });
        setSugs(r.data?.suggestions || []);
      } catch { setSugs([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [draft, field]);
  const add = (val) => {
    const v = (val ?? draft).trim();
    if (!v) return;
    if (!items.includes(v)) onChange([...items, v]);
    setDraft('');
  };
  const over = recommended && items.length > recommended;
  return (
    <div data-testid={`icp-edit-${testid}`}>
      <p className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5" /> {label}
      </p>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {items.map((it, i) => (
          <span key={i}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border bg-primary/10 text-primary border-primary/20"
            data-testid={`icp-chip-${testid}-${i}`}>
            {it}
            <button onClick={() => onChange(items.filter((_, idx) => idx !== i))}
              className="hover:text-red-500 transition-colors" data-testid={`icp-chip-remove-${testid}-${i}`}>
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        {items.length === 0 && <span className="text-xs text-muted-foreground italic">None yet</span>}
      </div>
      {over && (
        <p className="text-[11px] text-sky-600 mb-2 flex items-start gap-1" data-testid={`icp-rec-hint-${testid}`}>
          <Lightbulb className="w-3 h-3 mt-0.5 shrink-0" />
          <span>Best practice: ~{recommended} {label.toLowerCase()} keeps reach sharp. You've added {items.length} — more broadens reach (all are counted).</span>
        </p>
      )}
      <div className="flex gap-1.5">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          data-testid={`icp-input-${testid}`}
          list={field ? `dl-${testid}` : undefined}
          className="flex-1 rounded-lg border border-input bg-background px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary"
        />
        {field && (
          <datalist id={`dl-${testid}`} data-testid={`icp-suggest-${testid}`}>
            {sugs.map((s, i) => <option key={i} value={s} />)}
          </datalist>
        )}
        <button onClick={() => add()} data-testid={`icp-add-${testid}`}
          className="inline-flex items-center px-2 rounded-lg border border-border hover:bg-muted transition-colors">
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

// Small pill toggle for buying-signal switches.
const SignalToggle = ({ on, onClick, icon: Icon, label, testid, impact }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    aria-pressed={on}
    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
      on
        ? 'bg-amber-500/15 text-amber-600 border-amber-500/40'
        : 'bg-muted/40 text-muted-foreground border-border hover:bg-muted'
    }`}
  >
    <Icon className="w-3.5 h-3.5" /> {label}
    {on && impact != null && (
      <span className="font-semibold" data-testid={`${testid}-impact`}>−{impact}%</span>
    )}
    <span className={`ml-0.5 w-6 h-3 rounded-full relative transition-colors ${on ? 'bg-amber-500' : 'bg-muted-foreground/30'}`}>
      <span className={`absolute top-0.5 w-2 h-2 rounded-full bg-white transition-all ${on ? 'left-3.5' : 'left-0.5'}`} />
    </span>
  </button>
);

const StatPill = ({ icon: Icon, value, label, approx, testid }) => (
  <div className="flex items-center gap-2 rounded-lg border border-border bg-background/60 px-3 py-2" data-testid={testid}>
    <Icon className="w-4 h-4 text-emerald-500 shrink-0" />
    <div className="min-w-0">
      <p className="text-sm font-semibold text-foreground leading-none">
        {value == null ? '—' : value.toLocaleString()}{approx && value != null ? '≈' : ''}
      </p>
      <p className="text-[10px] text-muted-foreground mt-0.5 truncate">{label}</p>
    </div>
  </div>
);

// Phase A — editable ICP targeting + LIVE addressable-pool funnel.
export const ICPTargetingEditor = ({ icp, onSaved }) => {
  const sp = (icp && icp.apollo_search_params) || {};
  const [titles, setTitles] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [locations, setLocations] = useState([]);
  const [seniorities, setSeniorities] = useState([]);
  const [companySizes, setCompanySizes] = useState([]);
  const [keyword, setKeyword] = useState('');
  const [signals, setSignals] = useState({ hiring: false, funded: false });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  // Live reach funnel state
  const [reach, setReach] = useState(null);        // people count
  const [breakdown, setBreakdown] = useState(null);
  const [impactPct, setImpactPct] = useState(null);
  const [perSignal, setPerSignal] = useState({});
  const [liveLoading, setLiveLoading] = useState(false);
  const debounceRef = useRef(null);

  const hydrate = useCallback(() => {
    setTitles(sp.titles || icp?.titles || []);
    setIndustries(sp.raw_industries || icp?.industries || []);
    setLocations(sp.raw_locations || sp.locations || icp?.locations || []);
    setSeniorities(sp.seniorities || icp?.seniorities || []);
    setCompanySizes(icp?.company_sizes || (icp?.profile?.firmographic?.company_sizes) || []);
    setKeyword(sp.keyword || '');
    setSignals({ hiring: !!(sp.signals && sp.signals.hiring), funded: !!(sp.signals && sp.signals.funded) });
    setDirty(false);
  }, [icp]);

  useEffect(() => { hydrate(); }, [hydrate]);

  // Core: re-probe live reach whenever any targeting input changes (debounced).
  const probeReach = useCallback(async () => {
    setLiveLoading(true);
    try {
      const r = await api.post(`${API}/api/brain/icp/estimate-reach`, {
        titles, industries, locations, seniorities,
        company_sizes: companySizes, keyword,
        hiring: signals.hiring, funded: signals.funded,
        breakdown: true,
      });
      const bd = r.data?.breakdown || null;
      setBreakdown(bd);
      setImpactPct(r.data?.impact_pct ?? null);
      setPerSignal(r.data?.per_signal_impact || {});
      setReach(bd ? (bd.contactable != null ? bd.contactable : bd.people) : null);
    } catch {
      setBreakdown(null); setReach(null); setImpactPct(null); setPerSignal({});
    } finally { setLiveLoading(false); }
  }, [titles, industries, locations, seniorities, companySizes, keyword, signals]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { probeReach(); }, 650);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [probeReach]);

  const markDirty = (setter) => (v) => { setter(v); setDirty(true); };
  const toggleSignal = (key) => { setSignals((s) => ({ ...s, [key]: !s[key] })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`${API}/api/brain/icp/refine-search`, {
        titles, industries, locations, seniorities,
        company_sizes: companySizes, keyword, signals,
      });
      toast.success('Targeting saved — the Brain will use it on the next run');
      setDirty(false);
      if (onSaved) await onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save targeting');
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-xl border bg-card p-5 shadow-sm" data-testid="icp-targeting-editor">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="font-semibold text-foreground flex items-center gap-2">
            <SlidersHorizontal className="w-4 h-4 text-primary" /> Tune Your Targeting
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Edit who the Brain hunts — reach updates live as you type. Changes drive lead discovery <span className="font-medium">and</span> daily auto-prospecting.
          </p>
        </div>
        <button
          onClick={save}
          disabled={!dirty || saving}
          data-testid="icp-save-targeting-btn"
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 transition-colors self-start"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {dirty ? 'Save changes' : 'Saved'}
        </button>
      </div>

      {/* LIVE reach funnel + breakdown + signal toggles */}
      <div className="mb-5 rounded-lg border border-emerald-500/25 bg-emerald-500/[0.05] px-4 py-3" data-testid="icp-live-reach">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-lg bg-emerald-500/10">
              <Radar className="w-4 h-4 text-emerald-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Live reach — updates as you edit</p>
              {liveLoading ? (
                <p className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Estimating…
                </p>
              ) : reach == null ? (
                <p className="text-sm font-medium text-foreground/80 flex items-center gap-1.5" data-testid="icp-live-unavailable">
                  <Sparkles className="w-3.5 h-3.5 text-emerald-500" /> Add targeting to see your reach
                </p>
              ) : (
                <p className="text-lg font-bold text-emerald-600 leading-none" data-testid="icp-live-reach-number">
                  {reach.toLocaleString()} <span className="text-xs font-medium text-muted-foreground">people match</span>
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2" data-testid="icp-signal-toggles">
            <SignalToggle on={signals.hiring} onClick={() => toggleSignal('hiring')}
              icon={Briefcase} label="Hiring now" testid="icp-signal-hiring" impact={perSignal.hiring} />
            <SignalToggle on={signals.funded} onClick={() => toggleSignal('funded')}
              icon={TrendingUp} label="Recently funded" testid="icp-signal-funded" impact={perSignal.funded} />
            {(signals.hiring || signals.funded) && impactPct != null && (
              <span data-testid="icp-signal-impact"
                className="text-[11px] font-semibold text-amber-600 px-1.5 py-0.5 rounded-full bg-amber-500/10">
                −{impactPct}% reach
              </span>
            )}
            <button onClick={probeReach} disabled={liveLoading}
              data-testid="icp-live-refresh-btn"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-lg border border-border hover:bg-muted transition-colors">
              <RefreshCw className={`w-3.5 h-3.5 ${liveLoading ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>
        </div>

        {/* Reach breakdown */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3" data-testid="icp-reach-breakdown-grid">
          <StatPill icon={Building2} value={breakdown?.companies} label="Companies" testid="icp-stat-companies" />
          <StatPill icon={Users2} value={breakdown?.people} label="People" testid="icp-stat-people" />
          <StatPill icon={Mail} value={breakdown?.contactable} label="Contactable" testid="icp-stat-verified" />
          <StatPill icon={Phone} value={breakdown?.with_phone} label="With phone" approx testid="icp-stat-phone" />
        </div>
        {(signals.hiring || signals.funded) && (
          <p className="text-[11px] text-amber-600 mt-2" data-testid="icp-signal-note">
            Signal filter on — showing only {signals.hiring ? 'actively-hiring' : ''}{signals.hiring && signals.funded ? ' + ' : ''}{signals.funded ? 'recently-funded' : ''} companies. This narrows the pool.
          </p>
        )}
        {breakdown?.with_phone != null && (
          <p className="text-[10px] text-muted-foreground/70 mt-1">“With phone” is an estimate (sampled coverage ≈{Math.round((breakdown.phone_rate || 0) * 100)}%).</p>
        )}
      </div>

      {/* Editable chip grid — soft recommendations, no hard caps. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
        <ChipField label="Target titles" icon={Users2} items={titles} recommended={10} field="titles"
          onChange={markDirty(setTitles)} placeholder="e.g. VP Sales" testid="titles" />
        <ChipField label="Industries" icon={Building2} items={industries} recommended={5} field="industries"
          onChange={markDirty(setIndustries)} placeholder="e.g. SaaS" testid="industries" />
        <ChipField label="Locations" icon={MapPin} items={locations} recommended={5} field="locations"
          onChange={markDirty(setLocations)} placeholder="e.g. United States" testid="locations" />
        <ChipField label="Seniorities" icon={BadgeCheck} items={seniorities} recommended={6} field="seniorities"
          onChange={markDirty(setSeniorities)} placeholder="e.g. c_suite, vp, director" testid="seniorities" />
        <ChipField label="Company sizes" icon={Boxes} items={companySizes} field="company_sizes"
          onChange={markDirty(setCompanySizes)} placeholder="e.g. 11-50, 51-200" testid="company-sizes" />
        <div data-testid="icp-edit-keyword">
          <p className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1.5">
            <Search className="w-3.5 h-3.5" /> Keyword (optional)
          </p>
          <input
            value={keyword}
            onChange={(e) => { setKeyword(e.target.value); setDirty(true); }}
            placeholder="Leave blank for the widest pool"
            data-testid="icp-input-keyword"
            className="w-full rounded-lg border border-input bg-background px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <p className="text-[11px] text-muted-foreground mt-1">
            Tip: a keyword narrows the pool sharply (industry is already filtered). Blank = broadest reach.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ICPTargetingEditor;
